import type { ButtonHTMLAttributes, KeyboardEvent, ReactNode } from "react";
import { CircleCheck, Info, LoaderCircle, TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { avatarTone, initialsOf } from "../lib/format.js";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-flow text-ink hover:bg-flow-bright",
  secondary: "border border-line-strong bg-ink-3 text-foam hover:border-foam/25 hover:bg-ink-4",
  ghost: "text-mist hover:bg-foam/[0.06] hover:text-foam",
  danger: "border border-buoy/40 bg-buoy/[0.06] text-buoy hover:bg-buoy/15"
};

const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: "h-8 gap-1.5 px-3 text-[0.8125rem]",
  md: "h-9 gap-2 px-3.5 text-sm",
  lg: "h-11 gap-2 px-5 text-[0.9375rem]"
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant | undefined;
  readonly size?: ButtonSize | undefined;
  readonly icon?: LucideIcon | undefined;
  readonly loading?: boolean | undefined;
}

export function Button(props: ButtonProps): ReactNode {
  const { variant = "primary", size = "md", icon: Icon, loading = false, disabled, className = "", children, ...rest } = props;
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-semibold transition-colors duration-150 disabled:pointer-events-none disabled:opacity-45 ${BUTTON_SIZE[size]} ${BUTTON_VARIANT[variant]} ${className}`}
    >
      {loading ? <LoaderCircle size={15} className="animate-spin" aria-hidden="true" /> : null}
      {!loading && Icon !== undefined ? <Icon size={15} aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

/** Same look as Button, for router links and anchors that act as calls to action. */
export function buttonClass(variant: ButtonVariant = "primary", size: ButtonSize = "md"): string {
  return `inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-semibold transition-colors duration-150 ${BUTTON_SIZE[size]} ${BUTTON_VARIANT[variant]}`;
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly tone?: "default" | "danger" | undefined;
  readonly size?: "sm" | "md" | undefined;
}

export function IconButton(props: IconButtonProps): ReactNode {
  const { label, icon: Icon, tone = "default", size = "md", className = "", ...rest } = props;
  return (
    <button
      type="button"
      title={label}
      {...rest}
      aria-label={label}
      className={`inline-flex shrink-0 items-center justify-center rounded-lg text-mist transition-colors duration-150 hover:bg-foam/[0.06] disabled:pointer-events-none disabled:opacity-45 ${size === "sm" ? "size-8" : "size-9"} ${tone === "danger" ? "hover:text-buoy" : "hover:text-foam"} ${className}`}
    >
      <Icon size={size === "sm" ? 15 : 17} aria-hidden="true" />
    </button>
  );
}

export function Card(props: { readonly children: ReactNode; readonly className?: string | undefined }): ReactNode {
  return <div className={`rounded-xl border border-line bg-ink-2 ${props.className ?? ""}`}>{props.children}</div>;
}

export function CardHeader(props: {
  readonly title: string;
  readonly description?: string | undefined;
  readonly icon?: LucideIcon | undefined;
  readonly action?: ReactNode;
}): ReactNode {
  const Icon = props.icon;
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
      <div className="flex min-w-0 items-start gap-3">
        {Icon !== undefined ? (
          <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-line bg-ink-3 text-flow">
            <Icon size={16} aria-hidden="true" />
          </span>
        ) : null}
        <div className="min-w-0">
          <h2 className="text-[0.95rem] font-semibold text-foam">{props.title}</h2>
          {props.description !== undefined ? <p className="mt-0.5 text-sm text-mist">{props.description}</p> : null}
        </div>
      </div>
      {props.action}
    </div>
  );
}

/** Standard page frame: width, padding, and header rhythm shared by every app screen. */
export function Page(props: { readonly children: ReactNode; readonly width?: "narrow" | "default" | "wide" | undefined }): ReactNode {
  const width = props.width === "narrow" ? "max-w-[880px]" : props.width === "wide" ? "max-w-[1280px]" : "max-w-[1120px]";
  return <div className={`mx-auto w-full ${width} px-5 py-7 sm:px-8 lg:py-9`}>{props.children}</div>;
}

export function PageHeader(props: {
  readonly eyebrow?: string | undefined;
  readonly title: string;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
  readonly meta?: ReactNode;
  readonly divider?: boolean | undefined;
}): ReactNode {
  return (
    <header className={`flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between ${props.divider === false ? "pb-5" : "border-b border-line pb-6"}`}>
      <div className="min-w-0">
        {props.eyebrow !== undefined ? (
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-flow">{props.eyebrow}</p>
        ) : null}
        <h1 className="font-display mt-1.5 truncate text-[1.75rem] font-bold leading-tight tracking-[-0.015em] text-foam sm:text-[2rem]">
          {props.title}
        </h1>
        {props.description !== undefined ? <div className="mt-1.5 max-w-2xl text-sm leading-relaxed text-mist">{props.description}</div> : null}
        {props.meta !== undefined ? <div className="mt-3 flex flex-wrap items-center gap-2">{props.meta}</div> : null}
      </div>
      {props.actions !== undefined ? <div className="flex shrink-0 flex-wrap items-center gap-2">{props.actions}</div> : null}
    </header>
  );
}

/** Settings-style row: label and explanation on the left, controls on the right. */
export function Section(props: {
  /** Anchor for links into a settings section. */
  readonly id?: string | undefined;
  readonly title: string;
  readonly description?: ReactNode;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section id={props.id} className="grid scroll-mt-20 gap-5 border-b border-line py-8 last:border-b-0 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] md:gap-10">
      <div>
        <h2 className="text-[0.95rem] font-semibold text-foam">{props.title}</h2>
        {props.description !== undefined ? <div className="mt-1 text-sm leading-relaxed text-mist">{props.description}</div> : null}
        {props.aside !== undefined ? <div className="mt-3">{props.aside}</div> : null}
      </div>
      <div className="min-w-0">{props.children}</div>
    </section>
  );
}

export type BadgeTone = "neutral" | "mint" | "buoy" | "solid";

const BADGE_TONE: Record<BadgeTone, string> = {
  neutral: "border-line-strong bg-foam/[0.03] text-mist",
  mint: "border-flow/25 bg-flow/10 text-flow",
  buoy: "border-buoy/30 bg-buoy/10 text-buoy",
  solid: "border-transparent bg-foam text-ink"
};

export function Badge(props: { readonly tone?: BadgeTone | undefined; readonly icon?: LucideIcon | undefined; readonly children: ReactNode }): ReactNode {
  const Icon = props.icon;
  return (
    <span className={`inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 text-xs font-medium ${BADGE_TONE[props.tone ?? "neutral"]}`}>
      {Icon !== undefined ? <Icon size={12} aria-hidden="true" /> : null}
      {props.children}
    </span>
  );
}

const AVATAR_TONE: ReadonlyArray<string> = [
  "bg-flow/12 text-flow",
  "bg-sky-400/12 text-sky-300",
  "bg-violet-400/12 text-violet-300",
  "bg-amber-400/12 text-amber-300",
  "bg-rose-400/12 text-rose-300",
  "bg-lime-400/12 text-lime-300"
];

export function Avatar(props: { readonly label: string; readonly size?: "sm" | "md" | "lg" | undefined }): ReactNode {
  const tone = AVATAR_TONE[avatarTone(props.label)] ?? AVATAR_TONE[0];
  const size = props.size ?? "md";
  const dims = size === "lg" ? "size-12 rounded-xl text-base" : size === "sm" ? "size-7 rounded-md text-[0.7rem]" : "size-9 rounded-lg text-[0.8rem]";
  return (
    <span aria-hidden="true" className={`grid shrink-0 place-items-center font-bold ${dims} ${tone}`}>
      {initialsOf(props.label)}
    </span>
  );
}

export function Kbd(props: { readonly children: ReactNode }): ReactNode {
  return (
    <kbd className="inline-flex min-w-[1.3rem] items-center justify-center rounded border border-line-strong bg-ink-3 px-1.5 py-0.5 font-mono text-[0.68rem] leading-none text-mist">
      {props.children}
    </kbd>
  );
}

export type AlertTone = "info" | "success" | "danger";

const ALERT_TONE: Record<AlertTone, { readonly box: string; readonly icon: LucideIcon; readonly iconClass: string }> = {
  info: { box: "border-line-strong bg-ink-3/60", icon: Info, iconClass: "text-flow" },
  success: { box: "border-flow/25 bg-flow/[0.07]", icon: CircleCheck, iconClass: "text-flow" },
  danger: { box: "border-buoy/35 bg-buoy/[0.07]", icon: TriangleAlert, iconClass: "text-buoy" }
};

export function Alert(props: { readonly tone?: AlertTone | undefined; readonly children: ReactNode; readonly live?: boolean | undefined }): ReactNode {
  const tone = ALERT_TONE[props.tone ?? "info"];
  const Icon = tone.icon;
  return (
    <div
      role={props.tone === "danger" ? "alert" : "status"}
      className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 text-sm leading-relaxed text-foam ${tone.box}`}
    >
      <Icon size={16} className={`mt-0.5 shrink-0 ${tone.iconClass}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">{props.children}</div>
    </div>
  );
}

/** Inline destructive confirmation used in place of a modal for single-step deletes. */
export function ConfirmInline(props: {
  readonly message: ReactNode;
  readonly confirmLabel: string;
  readonly pending?: boolean | undefined;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  return (
    <div role="alertdialog" aria-label="Bestätigung" className="flex flex-col gap-3 rounded-lg border border-buoy/35 bg-buoy/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-foam">{props.message}</p>
      <div className="flex shrink-0 gap-2">
        <Button variant="ghost" size="sm" onClick={props.onCancel}>
          Abbrechen
        </Button>
        <Button variant="danger" size="sm" loading={props.pending} onClick={props.onConfirm}>
          {props.confirmLabel}
        </Button>
      </div>
    </div>
  );
}

export function EmptyState(props: {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
}): ReactNode {
  const Icon = props.icon;
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-line-strong px-6 py-14 text-center">
      <span className="grid size-11 place-items-center rounded-xl border border-line bg-ink-3 text-flow">
        <Icon size={20} aria-hidden="true" />
      </span>
      <h2 className="mt-4 text-base font-semibold text-foam">{props.title}</h2>
      {props.children !== undefined ? <div className="mt-1.5 max-w-sm text-sm leading-relaxed text-mist">{props.children}</div> : null}
      {props.action !== undefined ? <div className="mt-5">{props.action}</div> : null}
    </div>
  );
}

export function Skeleton(props: { readonly className?: string | undefined }): ReactNode {
  return <div aria-hidden="true" className={`animate-shimmer rounded-lg bg-ink-3 ${props.className ?? ""}`} />;
}

export function Spinner(props: { readonly label: string }): ReactNode {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-mist">
      <LoaderCircle size={16} className="animate-spin text-flow" aria-hidden="true" />
      <span>{props.label}</span>
    </span>
  );
}

export function Ring(props: { readonly value: number; readonly label: string; readonly size?: number | undefined }): ReactNode {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, props.value));
  const offset = circumference * (1 - clamped / 100);
  const color = clamped >= 80 ? "text-flow" : clamped >= 50 ? "text-mist" : "text-buoy";
  return (
    <svg
      role="img"
      aria-label={props.label}
      width={props.size ?? 88}
      height={props.size ?? 88}
      viewBox="0 0 80 80"
      className="shrink-0"
    >
      <circle cx="40" cy="40" r={radius} fill="none" stroke="currentColor" strokeWidth="6" className="text-ink-4" />
      <circle
        cx="40"
        cy="40"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90 40 40)"
        className={`${color} transition-[stroke-dashoffset] duration-700 ease-flow`}
      />
    </svg>
  );
}

export interface SegmentOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon?: LucideIcon | undefined;
  readonly disabled?: boolean | undefined;
}

function arrowSelect<T extends string>(
  event: KeyboardEvent<HTMLDivElement>,
  options: ReadonlyArray<SegmentOption<T>>,
  value: T,
  onChange: (value: T) => void
): void {
  const enabled = options.filter((option: SegmentOption<T>): boolean => option.disabled !== true);
  const index = enabled.findIndex((option: SegmentOption<T>): boolean => option.value === value);
  if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
    return;
  }
  event.preventDefault();
  const step = event.key === "ArrowRight" ? 1 : -1;
  const next = enabled[(index + step + enabled.length) % enabled.length];
  if (next !== undefined) {
    onChange(next.value);
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>("button");
    const position = options.findIndex((option: SegmentOption<T>): boolean => option.value === next.value);
    buttons[position]?.focus();
  }
}

export function Segmented<T extends string>(props: {
  readonly label: string;
  readonly value: T;
  readonly options: ReadonlyArray<SegmentOption<T>>;
  readonly onChange: (value: T) => void;
  readonly className?: string | undefined;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={props.label}
      onKeyDown={(event): void => arrowSelect(event, props.options, props.value, props.onChange)}
      className={`inline-flex items-center gap-1 rounded-lg border border-line bg-ink p-1 ${props.className ?? ""}`}
    >
      {props.options.map((option: SegmentOption<T>): ReactNode => {
        const active = option.value === props.value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            disabled={option.disabled}
            onClick={(): void => props.onChange(option.value)}
            className={`inline-flex h-8 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors duration-150 disabled:opacity-40 ${
              active ? "bg-ink-4 text-foam shadow-card" : "text-mist hover:text-foam"
            }`}
          >
            {Icon !== undefined ? <Icon size={14} aria-hidden="true" /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Underlined section tabs. Behaves like Segmented (radio semantics, arrow keys). */
export function Tabs<T extends string>(props: {
  readonly label: string;
  readonly value: T;
  readonly options: ReadonlyArray<SegmentOption<T>>;
  readonly onChange: (value: T) => void;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={props.label}
      onKeyDown={(event): void => arrowSelect(event, props.options, props.value, props.onChange)}
      className="flex items-center gap-1 overflow-x-auto border-b border-line"
    >
      {props.options.map((option: SegmentOption<T>): ReactNode => {
        const active = option.value === props.value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            disabled={option.disabled}
            onClick={(): void => props.onChange(option.value)}
            className={`relative -mb-px inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm font-medium transition-colors duration-150 disabled:opacity-40 ${
              active ? "border-flow text-foam" : "border-transparent text-mist hover:text-foam"
            }`}
          >
            {Icon !== undefined ? <Icon size={15} aria-hidden="true" className={active ? "text-flow" : undefined} /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
